import { useState } from 'react';
// @mui
import { styled, keyframes } from '@mui/material/styles';
import { Input, Box, Paper, Typography, CircularProgress } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
// utils
import { useNavigate, useLocation } from 'react-router-dom';
import call from '../../../core/services/http';
import { bgBlur } from '../../../utils/cssStyles';

// ----------------------------------------------------------------------

// Pulsing cursor animation
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
`;

const StyledSearchbar = styled('div')(({ theme, isActive }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '800px',
  height: '40px',
  padding: theme.spacing(0, 2),
  boxShadow: theme.customShadows.z8,
  borderRadius: '20px',
  border: isActive ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    border: `2px solid ${theme.palette.primary.light}`,
  },
  // Responsive design - make it smaller on mobile
  [theme.breakpoints.down('md')]: {
    width: '300px',
    height: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '250px',
    height: '32px',
  },
}));

const StyledInput = styled(Input)(({ theme, isActive }) => ({
  fontSize: '1rem',
  fontWeight: 'fontWeightRegular',
  width: '100%',
  '& .MuiInput-input': {
    padding: theme.spacing(1, 2),
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.8,
    },
  },
  '& .MuiInput-input:focus': {
    '&::after': {
      content: '""',
      position: 'absolute',
      right: '8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '2px',
      height: '16px',
      backgroundColor: theme.palette.primary.main,
      animation: isActive ? `${pulse} 1s infinite` : 'none',
    },
  },
  // Responsive font size
  [theme.breakpoints.down('md')]: {
    fontSize: '0.9rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

// ----------------------------------------------------------------------

export default function Searchbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFocus = () => {
    setIsActive(true);
  };

  const handleBlur = () => {
    setIsActive(false);
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response = await call('get', `/assets/search/?query=${query}`);
      setResults(response.data.data);
    } catch (err) {
      setError('Failed to fetch assets.');
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setIsActive(false);
    setResults([]);
    setQuery('');
    setError('');
  };

  const handleResultClick = (asset) => {
    navigate(`/dashboard/assets/${asset.id}`);
    // Clear results and query but keep the input focused (isActive stays true)
    setResults([]);
    setQuery('');
    setError('');
  };

  // Only show searchbar on assets route
  if (!location.pathname.includes('/dashboard/assets')) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={clearResults}>
      <Box sx={{ position: 'relative' }}>
        <StyledSearchbar isActive={isActive}>
          <StyledInput
            autoFocus
            disableUnderline
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!isActive) setIsActive(true);
            }}
            placeholder="Search assets…"
            isActive={isActive}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </StyledSearchbar>

        {/* Results dropdown */}
        {isActive && (results.length > 0 || loading || error) && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              zIndex: 1000,
              maxHeight: '400px',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {loading && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && (
              <Typography color="error" sx={{ p: 2 }}>
                {error}
              </Typography>
            )}

            {results?.map((asset) => (
              <Paper
                key={asset.id}
                sx={{
                  p: 2,
                  m: 1,
                  borderRadius: 1,
                  boxShadow: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => handleResultClick(asset)}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {asset.name}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Barcode:</strong> {asset.barcode_number || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Serial No:</strong> {asset.serial_number || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Location:</strong> {asset.location?.name || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Custodian:</strong> {asset.custodian?.display_name || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Asset Tag:</strong> {asset.asset_tag || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Status:</strong> {asset.status}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}
